/**
 * Universal, feed-aligned posts counts for a batch of events (T-8 ruling,
 * revised by the 2026-07-12 guest-conversion ruling: EVERY viewer — guest,
 * authed non-member, member — sees the same universal activity count per
 * event; membership unlocks READING the posts, not the number). Counts come
 * from the dedicated `GET /events/feed-counts` endpoint — never from a
 * list-payload field, which counted link-visible posts and could disagree
 * with the feed in both directions. The endpoint is public and the count
 * VALUE is viewer-independent; only WHICH event ids appear in a response
 * still honors per-viewer event visibility (unlisted/private events are
 * silently omitted for viewers who can't see them — those ids read as
 * `undefined`, exactly like a count that hasn't loaded).
 *
 * ONE call per SURFACE: hosts batch the ids of every event they render
 * (Home pill, space-slot pill, a timeline's live-window cards) instead of
 * fetching per card. The key is normalized — sorted, deduped, capped at the
 * endpoint's 30-id limit (`eventsFeedCountsKey`) — so two surfaces batching
 * the same set share one cache entry and one request, whatever the call
 * order. The key carries NO auth scope on purpose: the number is identical
 * for every identity, so guest and authed sessions sharing an entry is
 * correct. Auth transitions stay honest through the app-level cache
 * handling, not this hook — sign-out runs `queryClient.clear()`
 * (`clearAuthState`), web sign-in is a full-page redirect (fresh
 * QueryClient), and native sign-in invalidates `eventsBaseKey()`
 * (`useNativePostAuth`), whose prefix sweeps this family — refreshing the
 * per-viewer id-visibility omissions above.
 *
 * Loading contract (skeleton-for-counts-only): surfaces render instantly and
 * only the posts segment waits. The skeleton window is PER ID:
 * `isCountPending(id)` is true only during the FIRST fetch of the
 * batch AND only for ids actually inside the normalized batch — an id this
 * batch is not fetching (capped out at 30, outside the host's snapshot)
 * renders nothing, exactly like an omitted id, never a skeleton that can only
 * resolve to nothing. `placeholderData: keepPreviousData` keeps resolved
 * counts on screen across refetches and id-list growth, so the skeleton can
 * never reappear after first resolve. Flag-off / empty batches are
 * disabled: no fetch, no skeleton, no counts. A failed fetch degrades the
 * same way (counts are an enhancement — the surface itself never blocks on
 * them).
 */
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { getEventFeedCounts } from '@api-client/api-client';
import { featureFlags } from '@config/featureFlags';
import { LIVE_EVENTS_CONFIG } from '@config/liveEvents';
import { eventsFeedCountsKey } from '@lib/cache/events';

export interface UseEventFeedCountsResult {
  /**
   * The event's universal posts count for an id in THIS hook's batch.
   * `undefined` means "no count to show": still loading, omitted by the
   * backend (the viewer cannot see that event), outside the batch, or the
   * fetch is disabled (flag off / empty batch). Consumers keep their ≥ 1
   * render gate — `undefined` and `0` both render nothing.
   */
  getFeedCount: (eventId: string) => number | undefined;
  /**
   * Is THIS event's count in its skeleton window? True only while the
   * FIRST fetch of the batch is in flight (no data yet) AND the id is
   * actually inside the normalized batched set. Ids the batch is not fetching
   * — dropped by the 30-id cap, or outside the batch entirely — read false,
   * so their slot renders nothing (same as an omitted id) instead of a
   * skeleton that can never resolve to a count. Never true for
   * disabled batches, and never true again after first resolve (refetches and
   * id growth serve previous data via `placeholderData`).
   */
  isCountPending: (eventId: string) => boolean;
}

export function useEventFeedCounts(eventIds: readonly string[]): UseEventFeedCountsResult {
  // ONE normalization per render: the cache key IS the single source of truth,
  // and its normalized id array (`key[2]`) is exactly what the request asks for.
  // Deriving `ids` from the key (not a second `normalizeEventFeedCountIds` call)
  // keeps the entry a call is keyed by and the ids it fetches strictly aligned,
  // and runs the O(n log n) sort once — not once for `enabled`/the request and
  // again inside the key on every render (this hook re-renders per 30s tick).
  const key = eventsFeedCountsKey(eventIds);
  const ids = key[2];
  // No auth gate (2026-07-12 ruling): guests fetch too — the count is the
  // event's universal activity number and the endpoint is public; the API
  // alone decides which event ids a viewer may see. Only the kill switch and
  // an empty batch disable the query.
  const enabled = featureFlags.liveEvents && ids.length > 0;

  const { data, isLoading } = useQuery({
    queryKey: key,
    queryFn: async () => {
      const response = await getEventFeedCounts(ids);
      return response.data;
    },
    enabled,
    staleTime: LIVE_EVENTS_CONFIG.railStaleTimeMs,
    placeholderData: keepPreviousData,
    // Counts are a non-blocking enhancement (see the module docstring): a failed
    // fetch must degrade immediately, not hold the skeleton window open through
    // the app's default auth-aware 3-retry backoff (~7s) or pile retries onto a
    // struggling backend. The surface never blocks on this query.
    retry: false,
  });

  const counts = data?.counts;

  return {
    getFeedCount: (eventId: string) => counts?.[eventId],
    // Per-id pending = isLoading AND batch membership. isLoading (= v5
    // isPending && isFetching) rather than isPending: a disabled query is
    // "pending" forever in v5 terms but never fetching — a flag-off or empty
    // batch must not show a skeleton. The membership gate (`ids` is the
    // normalized ≤30-id batch) keeps the skeleton honest per card: an id this
    // batch is not fetching (30-cap overflow, a host's stale live-window
    // snapshot) shows nothing, never a skeleton that can only resolve to
    // nothing.
    isCountPending: (eventId: string) => isLoading && ids.includes(eventId),
  };
}
